import axios from 'axios';
import appConfig from '../configs/app.config';
import deepParseJson from '../utils/deepParseJson';
import store from '../store';
import { onSignOutSuccess } from '../store/auth/sessionSlice';
import Notification from '../components/ui/Notification';
import toast from '../components/ui/toast/toast';
import { safeGetItem } from '../utils/safeStorage';

const TOKEN_TYPE = 'Bearer ';
const REQUEST_HEADER_AUTH_KEY = 'Authorization';
const unauthorizedCode = [401];
const { unAuthenticatedEntryPath, apiPrefix } = appConfig;

const BaseService = axios.create({
  timeout: 60000,
  baseURL: apiPrefix,
});

BaseService.interceptors.request.use(
  (config) => {
    // Reading localStorage throws SecurityError on iOS Safari with
    // "Block All Cookies". Unguarded, every request died in this
    // interceptor and surfaced only as an "Operation Failed!" toast with no
    // Sentry event (ORDER-THISWAY-24). No token simply means no header.
    const rawPersistData = safeGetItem('user');
    const persistData = deepParseJson(rawPersistData);

    const accessToken = persistData?.auth?.session?.token;

    if (accessToken) {
      config.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${accessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

BaseService.interceptors.response.use(
  // A cancelled XHR (page unload, navigating away, a network switch — mostly
  // Firefox) reaches axios' settle() with `status: 0`, and settle RESOLVES
  // any falsy status instead of rejecting. Collapse it into the same
  // `undefined` the error handler below returns, so callers never receive
  // `{ status: 0, data: '' }` and dereference it (ORDER-THISWAY-28).
  (response) => (response && response.status === 0 ? undefined : response),
  (error) => {
    const { response } = error;

    // Always resolve a STRING message — never an object.
    // Rendering an object (e.g. { StatusCode, Message }) as a React child
    // throws "Objects are not valid as a React child" (ORDER-THISWAY-1H).
    const message =
      (typeof response?.data === 'string' && response.data) ||
      response?.data?.Message ||
      response?.data?.message ||
      error?.message ||
      'Something went wrong. Please try again.';

    // `error.response` is undefined for network errors / timeouts / CORS
    // (common on mobile Safari). Guarding it here fixes ORDER-THISWAY-H
    // and its mis-grouped duplicate ORDER-THISWAY-1W.
    // A caller that can live with the failure (the pre-confirmation fee
    // quote, which must never interrupt a payment) passes `silent: true`
    // in its axios config; the answer is still `undefined` below.
    const silent = error?.config?.silent === true;
    if (!silent && window.location.pathname !== unAuthenticatedEntryPath) {
      toast.push(
        <Notification title="Operation Failed!" type="danger" duration={2000}>
          {message}
        </Notification>,
        { placement: 'top-center' }
      );
    }

    // Sign-out on 401 must NOT depend on the current path. The old `&&`
    // chain skipped this entirely when the user was on `/home`.
    if (response && unauthorizedCode.includes(response.status)) {
      store.dispatch(onSignOutSuccess());
      window.location.href = '/';
    }

    // Intentionally NOT re-enabling `return Promise.reject(error)`.
    // Approach A (see design spec): preserve current behaviour — failed
    // requests resolve with `undefined` so existing callers are unaffected.
  }
);

export default BaseService;
